import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Plus, Minus } from 'lucide-react';
import FunctionList from './FunctionList';
import FunctionForm from './FunctionForm';
import { OrgFunction } from './FunctionTypes';
import organizationFunctionsService, {
    OrganizationFunction,
} from '../../services/organizationFunctionsService';
import FormControl from '../common/FormControl';
import Button from '../common/Button';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, confirmActive } from '../common/confirm';
import WorkflowModal from '../common/WorkflowModal';
import { usePermission } from '../../contexts/PermissionContext';
import NoDataFound from '../common/NoDataFound';
import StatusSwitch from '../common/StatusSwitch';
import extractApiError from '../../utils/apiError';

const RBAC_ACTIVITY = 'ORG_DEPARTMENTS';

type WorkflowState = 'draft' | 'submitted' | 'approved' | 'rejected';
type WorkflowFields = {
    workflow_state: WorkflowState;
    submitted_by?: any; submitted_on?: any;
    approved_by?: any; approved_on?: any; approval_note?: any;
    rejected_by?: any; rejected_on?: any; rejection_reason?: any;
};
type FunctionWithWorkflow = OrgFunction & WorkflowFields;

interface FunctionManagementProps {
    compact?: boolean;
}

const dbToUi = (item: OrganizationFunction): FunctionWithWorkflow => ({
    id: String(item.id),
    name: item.name,
    parentId: item.parent_function_id ? String(item.parent_function_id) : 'root',
    description: item.description || '',
    code_seq: item.code_seq,
    code: item.code,
    is_active: item.is_active,
    createdAt: item.created_on,
    createdBy: item.created_by ? String(item.created_by) : undefined,
    updatedAt: item.modified_on || undefined,
    updatedBy: item.modified_by ? String(item.modified_by) : undefined,
    workflow_state: (item.workflow_state ?? 'draft') as WorkflowState,
    submitted_by: item.submitted_by,
    submitted_on: item.submitted_on,
    approved_by: item.approved_by,
    approved_on: item.approved_on,
    approval_note: item.approval_note,
    rejected_by: item.rejected_by,
    rejected_on: item.rejected_on,
    rejection_reason: item.rejection_reason,
});

const uiToDb = (data: Omit<OrgFunction, 'id'>): Partial<OrganizationFunction> => ({
    name: data.name,
    description: data.description || undefined,
    parent_function_id: data.parentId && data.parentId !== 'root' ? Number(data.parentId) : undefined,
});

const FunctionManagement: React.FC<FunctionManagementProps> = ({ compact = false }) => {
    const viewPerm = usePermission(RBAC_ACTIVITY, 'view');
    const modifyPerm = usePermission(RBAC_ACTIVITY, 'view+modify');
    const canView = viewPerm.allowed;
    const canModify = modifyPerm.allowed;
    const permsLoading = viewPerm.loading;

    const [functions, setFunctions] = useState<FunctionWithWorkflow[]>([]);
    // Approved + active departments only — powers the "Falls Under" parent picker
    // so drafts / pending / rejected departments never leak into it.
    const [parentOptions, setParentOptions] = useState<Array<{ id: number; name: string }>>([]);
    const [loading, setLoading] = useState(true);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingFunction, setEditingFunction] = useState<FunctionWithWorkflow | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState<'ACTIVE' | 'INACTIVE'>('ACTIVE');
    const [toast, setToast] = useState<ToastMessage | null>(null);
    const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
    const [workflowOpenFor, setWorkflowOpenFor] = useState<FunctionWithWorkflow | null>(null);

    // Load the full set once — the parent→child tree needs every row present
    // to nest children under their parents. Filtering + search happen client-side.
    const loadData = useCallback(async () => {
        if (!canView) return;
        try {
            setLoading(true);
            setToast(null);
            const items = await organizationFunctionsService.getAll({ status: 'ALL' });
            setFunctions(items.map(dbToUi));
        } catch (err: any) {
            setToast({ type: 'error', message: err.message || 'Failed to load departments' });
            console.error('Error loading departments:', err);
        } finally {
            setLoading(false);
        }
    }, [canView]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Rows that are a parent of at least one other row — drives Expand/Collapse All.
    const parentIds = useMemo(() => {
        const ids = new Set<number>();
        const parents = new Set(
            functions
                .map(f => (f.parentId && f.parentId !== 'root' ? Number(f.parentId) : null))
                .filter((p): p is number => p != null),
        );
        functions.forEach(f => { if (parents.has(Number(f.id))) ids.add(Number(f.id)); });
        return ids;
    }, [functions]);

    const isAllExpanded = parentIds.size > 0 && expandedIds.size >= parentIds.size;

    const handleToggleAll = () => {
        if (isAllExpanded) setExpandedIds(new Set());
        else setExpandedIds(new Set(parentIds));
    };

    const handleToggleExpand = (id: number) => {
        setExpandedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    // Client-side status + search filter. Applied to the flat list before the
    // FunctionList builds the tree from it.
    const filteredFunctions = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return functions.filter(f => {
            const matchesStatus = statusFilter === 'ACTIVE'
                ? Number(f.is_active) === 1
                : Number(f.is_active) !== 1;
            const matchesSearch = !term
                || f.name.toLowerCase().includes(term)
                || (f.code || '').toLowerCase().includes(term);
            return matchesStatus && matchesSearch;
        });
    }, [functions, statusFilter, searchTerm]);

    // Load the approved-only parent options once (independent of table pagination).
    const loadParentOptions = useCallback(async () => {
        if (!canView) return;
        try {
            const opts = await organizationFunctionsService.getParentOptions();
            setParentOptions(opts);
        } catch (err) {
            console.error('Error loading department parent options:', err);
        }
    }, [canView]);

    useEffect(() => {
        loadParentOptions();
    }, [loadParentOptions]);

    const handleOpenAdd = () => {
        if (!canModify) return;
        setEditingFunction(null);
        setIsModalOpen(true);
    };

    const handleOpenEdit = (func: OrgFunction) => {
        setEditingFunction({ ...func, workflow_state: (func.workflow_state ?? 'draft') as WorkflowState } as FunctionWithWorkflow);
        setIsModalOpen(true);
    };

    const handleToggleActive = async () => {
        if (!editingFunction || !canModify) return;
        if (editingFunction.workflow_state === 'approved') {
            setToast({ type: 'error', message: 'Record is approved and locked.' });
            return;
        }
        const next = editingFunction.is_active ? 0 : 1;
        const confirmFn = editingFunction.is_active ? confirmWarning : confirmActive;
        const ok = await confirmFn({
            title: next ? 'Reactivate this department?' : 'Inactivate this department?',
            text: next ? 'It will appear in active lists again.' : 'It will be hidden from active lists.',
            confirmText: next ? 'Yes, Reactivate' : 'Yes, Inactivate',
        });
        if (!ok) return;
        try {
            await organizationFunctionsService.update(Number(editingFunction.id), { is_active: next });
            setToast({ type: next ? 'success' : 'warning', message: next ? 'Reactivated.' : 'Inactivated.' });
            setIsModalOpen(false);
            await Promise.all([loadData(), loadParentOptions()]);
        } catch (err: any) {
            setToast({ type: 'error', message: extractApiError(err, 'Failed to update status.') });
        }
    };

    const handleSubmit = async (data: Omit<OrgFunction, 'id'>) => {
        try {
            const dbData = uiToDb(data);
            if (editingFunction) {
                await organizationFunctionsService.update(Number(editingFunction.id), dbData);
                setToast({ type: 'success', message: 'Department updated successfully.' });
            } else {
                await organizationFunctionsService.create(dbData);
                setToast({ type: 'success', message: 'Department created successfully.' });
            }
            setIsModalOpen(false);
            await Promise.all([loadData(), loadParentOptions()]);
        } catch (err: any) {
            const detail = err?.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail : (detail ? JSON.stringify(detail) : err?.message);
            setToast({ type: 'error', message: msg || 'Failed to save department.' });
            throw err;
        }
    };

    const closeWfModal = () => setWorkflowOpenFor(null);

    if (!permsLoading && !canView) {
        return (
            <NoDataFound
                title="Not authorised"
                message={viewPerm.message || 'You are not authorised to view Departments. Please contact your Super Admin.'}
            />
        );
    }

    return (
        <>
        <Toast toast={toast} onClose={() => setToast(null)} />
        <div className={`netbody-card flex flex-col h-full ${compact ? '' : 'min-h-[500px]'}`}>

            <div className="netbody-header">
                {/* Status — iOS-style on/off switch (on = active) */}
                <StatusSwitch
                    value={statusFilter === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE'}
                    onChange={next => setStatusFilter(next)}
                    noun="functions"
                    testId="SEL_function_status_filter"
                />
                <Button
                    variant="secondary-outline"
                    icon={isAllExpanded ? Minus : Plus}
                    onClick={handleToggleAll}
                    data-testid="BTN_function_toggle_expand_all"
                >
                    {isAllExpanded ? 'Collapse All' : 'Expand All'}
                </Button>
                <div className="flex items-center gap-2 ml-auto">
                    <FormControl.Input
                        type="text"
                        placeholder="Search departments..."
                        value={searchTerm}
                        onChange={e => setSearchTerm(e.target.value)}
                        leadingIcon={<Search size={14} />}
                        className="filter-search"
                        data-testid="INP_function_search"
                    />
                    {canModify && (
                        <Button variant="fill" icon={Plus} onClick={handleOpenAdd} data-testid="BTN_function_create">
                            Create
                        </Button>
                    )}
                </div>
            </div>

            <div className="netbody-content">
                <div className="scroll-container flex-1 overflow-hidden">
                    <FunctionList
                        functions={filteredFunctions}
                        loading={loading}
                        onEdit={handleOpenEdit}
                        expandedIds={expandedIds}
                        onToggleExpand={handleToggleExpand}
                        onWorkflow={(f: OrgFunction) => setWorkflowOpenFor({ ...f, workflow_state: (f.workflow_state ?? 'draft') as WorkflowState } as FunctionWithWorkflow)}
                    />
                </div>
            </div>

            <FunctionForm
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSubmit={handleSubmit}
                initialData={editingFunction}
                functions={functions}
                parentOptions={parentOptions}
                onToggleActive={editingFunction && canModify ? handleToggleActive : undefined}
                canModify={canModify}
                isApproved={editingFunction?.workflow_state === 'approved'}
            />
        </div>

        <WorkflowModal
            isOpen={!!workflowOpenFor}
            onClose={closeWfModal}
            kind="org_function"
            id={workflowOpenFor ? Number(workflowOpenFor.id) : null}
            onChanged={() => { loadData(); loadParentOptions(); }}
        />
        </>
    );
};

export default FunctionManagement;
